import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { internalApi } from "@/react/helpers/urlHelpers/internalApi";
import {
  useMediaQuery,
  useSmOrMdScreenMediaQuery,
} from "@/react/hooks/useMediaQuery";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useWebSocket } from "@circle-react/hooks/useWebSocket";
import { Icon } from "@circle-react-shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Typography } from "@circle-react-uikit/Typography";
import { CIRCLE_KB_URL, VIRTUAL_CONVERSATION_ID } from "../constants";
import { useHelpWidgetContext } from "../context/HelpWidgetContext";
import { useHelpWidgetRoutes } from "../hooks/useHelpWidgetRoutes";
import { useShowConversation } from "../hooks/useShowConversation";
import { useShowPost } from "../hooks/useShowPost";
import type { HelpWidgetConversation } from "../types/help_widget_conversation";
import { popLocation } from "../utils/locationHistoryManager";
import { HeaderBackButton } from "./common/HeaderBackButton";
import { HeaderCloseButton } from "./common/HeaderCloseButton";
import { HeaderDownloadButton } from "./common/HeaderDownloadButton";
import { HeaderResizeButton } from "./common/HeaderResizeButton";
import { HeaderSidebarButton } from "./common/HeaderSidebarButton";
import { OpenInNewWindowLink } from "./common/OpenInNewWindowLink";
import "../assets/styles/custom-animations.css";

interface HeaderBarProps {
  onClose: () => void;
  pageName: string;
}

export const HeaderBar = ({ pageName, onClose }: HeaderBarProps) => {
  const [conversation, setConversation] =
    useState<HelpWidgetConversation | null>(null);
  const { isExpanded, isSidebarOpen, currentSpaceProperties } =
    useHelpWidgetContext();
  const location = useLocation();
  const history = useHistory();
  const {
    isSpaceGroupsRoutes,
    isSpaceRoute,
    postMatch,
    newsMatch,
    isPostRoute,
    isNewsRoute,
    isBackButtonVisible,
    isExpandButtonVisible,
    isSpecialHeaderVisible,
    isNewsListRoute,
    isEventsRoute,
    isSendUsMessageRoute,
    isMessageConfirmationRoute,
    isConversationsRoute,
    isConversationListRoute,
    singleConversationMatch,
    isSingleConversationRoute,
  } = useHelpWidgetRoutes();
  const isMobile = useSmOrMdScreenMediaQuery();
  const isLargeScreen = useMediaQuery("(min-width: 1080px)");

  const isBorderVisible = location.pathname !== "/";

  const { data: post } = useShowPost(
    postMatch?.params?.id || newsMatch?.params?.id || "",
  );

  const conversationId = singleConversationMatch?.params?.id || "";

  const { data: conversationData } = useShowConversation(conversationId);

  useEffect(() => {
    if (conversationData) {
      setConversation(conversationData);
    }
  }, [conversationData]);

  useWebSocket(
    {
      channel: "HelpWidget::ConversationChannel",
      onMessageReceive: (data: {
        event: string;
        conversation?: HelpWidgetConversation;
      }) => {
        if (data.event === "updateConversation" && data.conversation) {
          setConversation(data.conversation);
        }
      },
      conversation_id: conversationId,
      canCreateConnection: Boolean(conversationId),
    },
    [conversationId],
  );

  const handleBack = () => {
    if (isMessageConfirmationRoute) {
      history.replace("/");
    } else if (isSingleConversationRoute) {
      history.replace("/conversations");
    } else {
      const previousLocation = popLocation();
      history.replace(previousLocation);
    }
  };

  const getHeaderTitle = () => {
    if (isSpaceGroupsRoutes) return t("support_widget.search_container.title");
    if ((isNewsRoute || isPostRoute) && post) return post.name || "";
    if (isNewsListRoute) return t("support_widget.news.news");
    if (isSpaceRoute) return currentSpaceProperties.name;
    if (isEventsRoute) return t("support_widget.events");
    if (isSendUsMessageRoute || isMessageConfirmationRoute)
      return t("help_widget.report_bug.send_us_a_message");
    if (isConversationListRoute)
      return t("help_widget.conversation_list_page.title");
    if (isSingleConversationRoute)
      return conversation?.title || t("help_widget.conversation_page.title");
    return pageName;
  };

  const urls = ["help-en.circle.so", "help-pt.circle.so", "pt-help.circle.so"];
  let postKbUrl = post?.post_url || CIRCLE_KB_URL;
  urls.forEach(url => {
    if (postKbUrl.includes(url)) {
      postKbUrl = postKbUrl.replace(url, "help.circle.so");
    }
  });

  if (isSpecialHeaderVisible) {
    return (
      <div
        className={classNames(
          "bg-primary border-primary sticky top-0 z-10 flex h-16 w-full shrink-0 items-center border-b",
          "justify-between",
        )}
      >
        {isExpanded && (
          <div
            className={classNames(
              "border-primary flex h-full items-center gap-2 border-r px-5 transition-all duration-300 ease-in-out",
              {
                "w-[312px]": isSidebarOpen,
                "w-0 border-r-0 px-0": !isSidebarOpen,
              },
            )}
          >
            <div
              className={classNames(
                "ease-in-strong flex items-center opacity-100 transition-opacity duration-300",
                {
                  "ease-out-strong -z-10 opacity-0": !isSidebarOpen,
                },
              )}
            >
              <Typography.LabelMd weight="semibold" color="text-darkest">
                {t("help_widget.conversation_list_page.title")}
              </Typography.LabelMd>
            </div>
          </div>
        )}

        <div
          className={classNames("flex items-center gap-2 px-5", {
            "w-[736px]": isExpanded,
            "w-[400px]": !isExpanded,
            "w-full": isMobile,
          })}
        >
          {isBackButtonVisible && <HeaderBackButton onClick={handleBack} />}
          {isConversationsRoute && isExpanded && isLargeScreen && (
            <HeaderSidebarButton />
          )}
          <div className="min-w-0 flex-1 truncate">
            <Typography.LabelMd weight="semibold" color="text-darkest">
              {getHeaderTitle()}
            </Typography.LabelMd>
          </div>
          {isExpandButtonVisible && !isMobile && <HeaderResizeButton />}
          {isSingleConversationRoute &&
            conversationId &&
            conversationId !== VIRTUAL_CONVERSATION_ID && (
              <HeaderDownloadButton
                href={internalApi.helpWidget.downloadTranscript({
                  id: conversationId,
                })}
              />
            )}
          {(isPostRoute || isNewsRoute) && (
            <OpenInNewWindowLink version="minimal" postUrl={postKbUrl} />
          )}
          <HeaderCloseButton onClick={onClose} />
        </div>
      </div>
    );
  }

  // Default header with page name and close button
  return (
    <div
      className={classNames(
        "bg-primary sticky top-0 z-10 flex h-16 w-full shrink-0 items-center justify-between gap-2 px-5",
        { "border-primary border-b": isBorderVisible },
      )}
    >
      <div className="min-w-0 flex-1 truncate">
        <Typography.LabelMd weight="semibold" color="text-darkest">
          {getHeaderTitle()}
        </Typography.LabelMd>
      </div>
      <TippyV2 content={t("help_widget.tooltip.close")}>
        <button
          type="button"
          className="hover:bg-secondary text-light flex size-6 items-center rounded-full p-1"
          onClick={onClose}
        >
          <Icon type="hw-cross" size={16} />
        </button>
      </TippyV2>
    </div>
  );
};
